import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Button,
  MenuItem,
  InputAdornment,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  Business,
  LocationCity,
  Public,
  LocationOn,
  AccountBalance,
  WhatsApp,
  Lock,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import { checkEmail } from "../services/auth";
import { createClient } from "../services/source";
import { getCurrentUser } from "../services/auth";

const CreateClient = ({ onClose, onCreateSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRetypePassword, setShowRetypePassword] = useState(false);
  // const [touched, setTouched] = useState({});
  // const [errors, setErrors] = useState({});
  const countries = [
    "Angola",
    "Cabo Verde",
    "Moçambique",
    "Portugal",
    "Brasil",
  ];
  // Validation
  const validationSchema = Yup.object({
    nome: Yup.string().required("Nome é obrigatório"),
    email: Yup.string().email("Email inválido").required("Email é obrigatório"),
    password: Yup.string()
      .min(6, "Senha deve ter no mínimo 6 caracteres")
      .required("Senha é obrigatória"),
    retypePassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Senhas não coincidem")
      .required("Confirmação de senha é obrigatória"),
    telemovel: Yup.string()
      .matches(/^\d+$/, "Telefone inválido")
      .required("Telefone é obrigatório"),
    whatsapp: Yup.string()
      .matches(/^\d{9}$/, "WhatsApp deve ter 9 dígitos")
      .required("WhatsApp é obrigatório"),
    empresa: Yup.string().required("Empresa é obrigatória"),
    nif: Yup.string()
      .matches(/^\d{9}$/, "NIF deve conter 9 dígitos")
      .required("NIF é obrigatório"),
    cidade: Yup.string().required("Cidade é obrigatória"),
    pais: Yup.string().required("País é obrigatório"),
    morada: Yup.string().required("Morada é obrigatória"),
    status: Yup.string().required("Status é obrigatório"),
  });

  const user = getCurrentUser();
  const companyId = user?.data?.companyId || user?.data?.id;

  const formik = useFormik({
    initialValues: {
      nome: "",
      email: "",
      password: "",
      retypePassword: "",
      nif: "",
      telemovel: "",
      empresa: "",
      cidade: "",
      pais: "",
      morada: "",
      whatsapp: "",
      status: "Ativo",
    },

    validationSchema,

    onSubmit: async (values, { resetForm }) => {
      setError("");
      setSuccess("");

      try {
        setLoading(true);

        // verify email
        const emailCheck = await checkEmail({
          email: values.email,
        });

        if (emailCheck.exists) {
          formik.setFieldError("email", "Email já está registado");

          setLoading(false);
          return;
        }

        // create client
        const payload = {
          ...values,
          companyId,
        };
        const res = await createClient(payload);
        if (res) {
          setSuccess("Cliente criado com sucesso!");
          resetForm();
          // notify parent and close modal
          onCreateSuccess?.();
          onClose?.();
        } else {
          setError("Erro ao criar cliente");
        }
      } catch (err) {
        console.error(err);

        setError("Erro ao criar cliente");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleEmailBlur = async (e) => {
    formik.handleBlur(e);

    const email = e.target.value;

    if (!email) return;

    try {
      // validate email format first
      await validationSchema.validateAt("email", {
        email,
      });

      // check if email exists
      const data = await checkEmail({ email });

      if (data.exists) {
        formik.setFieldError("email", "Email já está registado");
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card
        elevation={5}
        sx={{
          maxWidth: 900,
          margin: "0 auto",
          borderRadius: 4,
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            background: "#5b2c72",
            color: "#fff",
            borderRadius: "16px 16px 0 0",
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" color="#fff">
              Criar Cliente
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Registe um novo cliente no sistema
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.1)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.2)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* NOME */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome Completo"
                  name="nome"
                  variant="filled"
                  value={formik.values.nome}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.nome && Boolean(formik.errors.nome)}
                  helperText={formik.touched.nome && formik.errors.nome}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* EMAIL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  variant="filled"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={handleEmailBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* PASSWORD */}
              <Grid item xs={12} md={6}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    fullWidth
                    label="Senha"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    variant="filled"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.password && Boolean(formik.errors.password)
                    }
                    helperText={
                      formik.touched.password && formik.errors.password
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: "#9c27b0" }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Box>
              </Grid>

              {/* CONFIRM PASSWORD */}
              <Grid item xs={12} md={6}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    fullWidth
                    label="Confirmar Senha"
                    name="retypePassword"
                    type={showRetypePassword ? "text" : "password"}
                    variant="filled"
                    value={formik.values.retypePassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.retypePassword &&
                      Boolean(formik.errors.retypePassword)
                    }
                    helperText={
                      formik.touched.retypePassword &&
                      formik.errors.retypePassword
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: "#9c27b0" }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <IconButton
                    onClick={() => setShowRetypePassword(!showRetypePassword)}
                    sx={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    {showRetypePassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Box>
              </Grid>

              {/* TELEFONE */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telemovel"
                  name="telemovel"
                  variant="filled"
                  value={formik.values.telemovel}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.telemovel && Boolean(formik.errors.telemovel)
                  }
                  helperText={
                    formik.touched.telemovel && formik.errors.telemovel
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* WHATSAPP */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="WhatsApp"
                  name="whatsapp"
                  variant="filled"
                  value={formik.values.whatsapp}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.whatsapp && Boolean(formik.errors.whatsapp)
                  }
                  helperText={formik.touched.whatsapp && formik.errors.whatsapp}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WhatsApp sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* NIF */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="NIF"
                  name="nif"
                  variant="filled"
                  value={formik.values.nif}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.nif && Boolean(formik.errors.nif)}
                  helperText={formik.touched.nif && formik.errors.nif}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalance sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* EMPRESA */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Empresa"
                  name="empresa"
                  variant="filled"
                  value={formik.values.empresa}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.empresa && Boolean(formik.errors.empresa)
                  }
                  helperText={formik.touched.empresa && formik.errors.empresa}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Business sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* CIDADE */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cidade"
                  name="cidade"
                  variant="filled"
                  value={formik.values.cidade}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.cidade && Boolean(formik.errors.cidade)}
                  helperText={formik.touched.cidade && formik.errors.cidade}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCity sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* PAIS */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="País"
                  name="pais"
                  variant="filled"
                  value={formik.values.pais}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.pais && Boolean(formik.errors.pais)}
                  helperText={formik.touched.pais && formik.errors.pais}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Public sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                >
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* MORADA */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Morada"
                  name="morada"
                  variant="filled"
                  value={formik.values.morada}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.morada && Boolean(formik.errors.morada)}
                  helperText={formik.touched.morada && formik.errors.morada}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* STATUS */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  variant="filled"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="Ativo">Ativo</MenuItem>

                  <MenuItem value="Inativo">Inativo</MenuItem>
                </TextField>
              </Grid>

              {/* BUTTON */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #9c27b0 0%, #6a1b9a 100%)",

                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #b533d0 0%, #7e1fa5 100%)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    "Criar Cliente"
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateClient;
